// Clickable variable chips — click a token like {{firstName}} and it drops
// into whichever text field you last touched, at the caret. Replaces the old
// "Variables: {{firstName}} …" static help text that forced select→copy→paste
// (Lissette 2026-07-24: "es muy fastidioso subrayar, copiar y luego pegar").
//
// The insert-at-cursor idiom was copy-pasted across ~5 editors (SignaturesView,
// MassEmailView, WorkflowsView, DocRequests, BirthdayGreetingComposer); this is
// the shared version so every remaining editor gets the SAME behavior.
//
// Usage:
//   const vi = useVarInserter();
//   <textarea value={v} onChange={e=>setV(e.target.value)} {...vi.bind(() => v, next => setV(next))} />
//   <input    value={s} onChange={e=>setS(e.target.value)} {...vi.bind(() => s, next => setS(next))} />
//   <VarChips vars={['{{firstName}}','{{lastName}}']} inserter={vi} lang={lang} />
//
// bind(getValue, setValue) wires a field: on focus it becomes the active target;
// a chip click splices the tag in at that field's caret. Chips use
// onMouseDown preventDefault so clicking one never blurs the field — focus and
// selection stay put, which is what makes the caret insert reliable.

import { useRef } from 'react';
import { crearInsertador } from '../insercionDeVariables.js';

export function useVarInserter() {
  // { el, getValue, setValue } para el campo que se tocó por última vez.
  const ref = useRef(null);
  // La lógica vive en `insercionDeVariables.js`, sin React: lo que se rompió
  // aquí (Crismar Perez, 2026-09-18) no se ve leyendo el código y sólo aparece
  // al reproducir los gestos de una persona, así que tenía que poder probarse.
  return crearInsertador(ref);
}

// Self-contained textarea + clickable variable chips, for the simple single-
// field spots (e.g. the extra-signature box) where wiring a useVarInserter hook
// into a big host component isn't worth it. Controlled via value/onChange.
export function TextareaWithVars({ value, onChange, vars = [], lang = 'es', label, textareaProps = {}, chipsStyle }) {
  const vi = useVarInserter();
  return (
    <>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        {...vi.bind(() => value || '', (v) => onChange(v))}
        {...textareaProps}
      />
      <VarChips vars={vars} inserter={vi} lang={lang} label={label} style={chipsStyle} />
    </>
  );
}

export function VarChips({ vars = [], inserter, lang = 'es', label, style }) {
  const es = lang === 'es';
  const lbl = label !== undefined ? label : (es ? 'Variables (haz clic para insertar):' : 'Variables (click to insert):');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', ...style }}>
      {lbl ? <span style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)' }}>{lbl}</span> : null}
      {vars.map((v) => {
        const tag = typeof v === 'string' ? v : v.tag;
        const text = typeof v === 'string' ? v : (v.label || v.tag);
        return (
          <button
            key={tag}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => inserter && inserter.insert(tag)}
            title={es ? 'Insertar en el campo' : 'Insert into the field'}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: '.64rem', color: 'var(--c-cyan,#00d4ff)',
              background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.25)',
              borderRadius: 6, padding: '.12rem .45rem', cursor: 'pointer', lineHeight: 1.5,
            }}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}
